import { useDashboardSummary, useTimeSeriesTrend } from '../../../../../hooks/useDataQuality'
import { MetricType, type TimeWindow } from '../../../../../types/dataQuality'
import { AreaChart, Area, ResponsiveContainer, YAxis, XAxis } from 'recharts'
import LoadingSkeleton from '../../../../common/LoadingSkeleton'
import ErrorDisplay from '../../../../common/ErrorDisplay'
import './TrendsOverTime.css'

interface TrendsOverTimeProps {
  timeWindow: TimeWindow
}

const TrendsOverTime = ({ timeWindow }: TrendsOverTimeProps) => {
  // Fetch dashboard summary for current values and trends
  const { data: dashboardData, isLoading: isDashboardLoading, error: dashboardError } = useDashboardSummary({
    time_window: timeWindow,
    comparison_days: 7
  })

  // Fetch time series data for each metric
  const { data: trustScoreTrend } = useTimeSeriesTrend({
    metric_type: MetricType.TRUST_SCORE,
    days_back: 7,
    granularity: 'daily',
  })

  const { data: completenessTrend } = useTimeSeriesTrend({
    metric_type: MetricType.COMPLETENESS_SCORE,
    days_back: 7,
    granularity: 'daily',
  })

  const { data: slaHealthTrend } = useTimeSeriesTrend({
    metric_type: MetricType.SLA_HEALTH_SCORE,
    days_back: 7,
    granularity: 'daily',
  })

  const { data: schemaDriftTrend } = useTimeSeriesTrend({
    metric_type: MetricType.SCHEMA_STABILITY_SCORE,
    days_back: 7,
    granularity: 'daily',
  })

  if (isDashboardLoading) {
    return (
      <div className="trends-over-time-section">
        <h3 className="trends-section-title">Trends Over Time</h3>
        <div className="trends-grid">
          <LoadingSkeleton variant="metric" count={4} />
        </div>
      </div>
    )
  }

  if (dashboardError) {
    return (
      <div className="trends-over-time-section">
        <ErrorDisplay
          error={dashboardError.message}
          onRetry={() => window.location.reload()}
          title="Failed to Load Trends"
        />
      </div>
    )
  }

  if (!dashboardData) {
    return (
      <div className="trends-over-time-section">
        <h3 className="trends-section-title">Trends Over Time</h3>
        <p className="no-data-message">
          No trend data available
          <span style={{ display: 'block', fontSize: '11px', color: '#9ca3af', marginTop: '8px' }}>
            Dashboard data is null. Check console for details.
          </span>
        </p>
      </div>
    )
  }

  // Helper function to format date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    return `${date.getMonth() + 1}/${date.getDate()}`
  }

  // Helper function to transform time series data for charts
  interface TimeSeriesPoint {
    date: string;
    value: number;
  }

  interface TimeSeriesData {
    chart_data?: TimeSeriesPoint[];
  }

  const transformChartData = (timeSeriesData: TimeSeriesData | undefined): Array<{ date: string; value: number }> => {
    if (!timeSeriesData?.chart_data || timeSeriesData.chart_data.length === 0) {
      return []
    }
    return timeSeriesData.chart_data.map((point: TimeSeriesPoint) => ({
      date: formatDate(point.date),
      value: point.value
    }))
  }

  // Get trend icon
  const getTrendIcon = (direction: string) => {
    if (direction === 'up') {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
          <polyline points="17 6 23 6 23 12"/>
        </svg>
      )
    } else if (direction === 'down') {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/>
          <polyline points="17 18 23 18 23 12"/>
        </svg>
      )
    }
    return null
  }

  // Find specific KPI summaries
  const completenessKPI = dashboardData.kpi_summaries?.find(kpi => kpi.metric_name === 'completeness_score')
  const slaHealthKPI = dashboardData.kpi_summaries?.find(kpi => kpi.metric_name === 'sla_health_score')

  // Calculate schema drift frequency (inverted - high stability = low drift)
  const schemaDriftFrequency = schemaDriftTrend?.summary?.average_value
    ? Math.max(0, 100 - schemaDriftTrend.summary.average_value)
    : 0

  const schemaDriftEvents = Math.round(schemaDriftFrequency / 10) // Convert to event count

  // Get trust score from overall_metrics or fallback to time series average
  const trustScoreValue = dashboardData.overall_metrics?.trust_score ?? trustScoreTrend?.summary?.average_value ?? 0
  const trustScoreTrendPct = dashboardData.overall_metrics?.trend_percentage ?? trustScoreTrend?.trend_statistics?.trend_percentage ?? 0
  const trustScoreTrendDir = dashboardData.overall_metrics?.trend_direction ?? (trustScoreTrendPct >= 0 ? 'up' : 'down')

  // Build trend cards
  const trends = [
    {
      title: 'Data Trust Score',
      value: `${Math.round(trustScoreValue)}`,
      unit: '/ 100',
      trend: trustScoreTrendPct,
      direction: trustScoreTrendDir,
      chartData: transformChartData(trustScoreTrend),
      color: trustScoreTrendPct >= 0 ? '#10b981' : '#ef4444'
    },
    {
      title: 'Completeness %',
      value: `${(completenessKPI?.current_value ?? completenessTrend?.summary?.average_value ?? 0).toFixed(1)}`,
      unit: '%',
      trend: completenessKPI?.trend_percentage ?? completenessTrend?.trend_statistics?.trend_percentage ?? 0,
      direction: (completenessKPI?.trend_percentage ?? completenessTrend?.trend_statistics?.trend_percentage ?? 0) >= 0 ? 'up' : 'down',
      chartData: transformChartData(completenessTrend),
      color: (completenessKPI?.trend_percentage ?? completenessTrend?.trend_statistics?.trend_percentage ?? 0) >= 0 ? '#10b981' : '#ef4444'
    },
    {
      title: 'SLA Health',
      value: `${(slaHealthKPI?.current_value ?? slaHealthTrend?.summary?.average_value ?? 0).toFixed(0)}`,
      unit: '%',
      trend: slaHealthKPI?.trend_percentage ?? slaHealthTrend?.trend_statistics?.trend_percentage ?? 0,
      direction: (slaHealthKPI?.trend_percentage ?? slaHealthTrend?.trend_statistics?.trend_percentage ?? 0) >= 0 ? 'up' : 'down',
      chartData: transformChartData(slaHealthTrend),
      color: (slaHealthKPI?.trend_percentage ?? slaHealthTrend?.trend_statistics?.trend_percentage ?? 0) >= 0 ? '#10b981' : '#ef4444'
    },
    {
      title: 'Schema Drift Frequency',
      value: `${schemaDriftEvents}`,
      unit: 'events',
      trend: schemaDriftTrend?.trend_statistics?.trend_percentage
        ? -Math.abs(schemaDriftTrend.trend_statistics.trend_percentage)
        : -100,
      direction: 'down',
      chartData: transformChartData(schemaDriftTrend).map(point => ({
        ...point,
        value: Math.max(0, 100 - point.value) / 10 // Invert and scale
      })),
      color: '#ef4444'
    }
  ]

  return (
    <div className="trends-over-time-section">
      <h3 className="trends-section-title">Trends Over Time</h3>

      <div className="trends-grid">
        {trends.map((trend, index) => (
          <div key={index} className="trend-card">
            <div className="trend-card-header">
              <h4 className="trend-card-title">{trend.title}</h4>
              <span className={`trend-change ${trend.direction}`}>
                {getTrendIcon(trend.direction)}
                <span className="trend-change-number">{Math.abs(trend.trend).toFixed(1)}</span>
                <span className="trend-change-symbol">%</span>
              </span>
            </div>

            <div className="trend-value-section">
              <span className="trend-value">{trend.value}</span>
              <span className="trend-unit">{trend.unit}</span>
            </div>

            <div className="trend-chart-container">
              {trend.chartData && trend.chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={100}>
                  <AreaChart 
                    data={trend.chartData} 
                    margin={{ top: 5, right: 5, left: 0, bottom: 5 }}
                  >
                    <defs>
                      <linearGradient id={`gradient-${index}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={trend.color} stopOpacity={0.3}/>
                        <stop offset="95%" stopColor={trend.color} stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <XAxis 
                      dataKey="date" 
                      hide={true}
                      type="category"
                    />
                    <YAxis 
                      hide={true} 
                      domain={['dataMin - 5', 'dataMax + 5']}
                      type="number"
                    />
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke={trend.color}
                      strokeWidth={2.5}
                      fill={`url(#gradient-${index})`}
                      animationDuration={800}
                      dot={{ r: 3, fill: trend.color, strokeWidth: 0 }}
                      activeDot={{ r: 5, fill: trend.color }}
                      isAnimationActive={true}
                      connectNulls={true}
                      fillOpacity={1}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="trend-no-chart">
                  <span>No data</span>
                </div>
              )}
            </div>

            <div className="trend-date-range">
              {trend.chartData && trend.chartData.length > 0 ? (
                <>
                  <span>{trend.chartData[0]?.date}</span>
                  <span>{trend.chartData[Math.floor(trend.chartData.length / 2)]?.date}</span>
                  <span>{trend.chartData[trend.chartData.length - 1]?.date}</span>
                </>
              ) : (
                <span>Last 7 days</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default TrendsOverTime
